import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faEye, faEyeSlash, faGem } from '@/icons';
import { SignupProgress, USER_SIGNUP_STEPS } from '../components/SignupProgress';
import { SignupBackButton } from '../components/SignupBackButton';
import { authApiService } from '../services/authApiService';
import { debugError } from '../utils/debug';

export const ApplicantTeamInvitePasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;

    const token = (searchParams.get('token') || '').trim();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [plan, setPlan] = useState<string>('');
    const [expiredNotified, setExpiredNotified] = useState(false);

    useEffect(() => {
        document.title = 'Create password | Calculist';
    }, []);

    if (flagsLoading) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;
    if (!token) return <Navigate to="/auth/login" replace />;

    const wrapper = (content: React.ReactNode) => (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
        }}>
            {content}
        </div>
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setSubmitting(true);
        try {
            const result = await authApiService.signupAcceptApplicantTeamInvite({ token, password, acceptTerms: true });
            setPlan(result.data?.plan ?? '');
            setDone(true);
        } catch (err: unknown) {
            debugError('signupAcceptApplicantTeamInvite', err);
            const msg = err instanceof Error ? err.message : 'Could not complete registration. Please try again.';
            if (msg.toLowerCase().includes('invalid or expired')) {
                try { await authApiService.signupNotifyExpiredInvite(token); } catch { /* best effort */ }
                setExpiredNotified(true);
            }
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return wrapper(
            <div style={{ ...themedStyles.card.default, maxWidth: '480px', width: '100%' }}>
                <SignupProgress currentStep={3} steps={USER_SIGNUP_STEPS} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <img src="/logo-name.svg" alt="Calculist" style={{ height: '48px', width: 'auto', marginBottom: theme.spacing.lg }} />
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: '72px', height: '72px', borderRadius: '50%',
                        backgroundColor: '#f0fdf4', color: '#16a34a', fontSize: '32px',
                        marginBottom: theme.spacing.md,
                    }}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Welcome to Calculist!</h1>
                    <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>Your account is ready. Sign in to get started.</p>
                </div>

                {plan === 'pro' && (
                    <div style={{ backgroundColor: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: theme.borderRadius.lg, padding: theme.spacing.md, marginBottom: theme.spacing.md }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.xs }}>
                            <FontAwesomeIcon icon={faGem} style={{ color: '#7c3aed', fontSize: '18px', flexShrink: 0 }} />
                            <span style={{ fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: '#6b21a8' }}>You're on the Pro plan</span>
                        </div>
                        <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: '#6b21a8' }}>
                            Look for the <FontAwesomeIcon icon={faGem} style={{ fontSize: '12px' }} /> icon throughout Calculist — it marks useful features exclusive to your Pro plan.
                        </p>
                    </div>
                )}

                {plan === 'basic' && (
                    <div style={{ backgroundColor: theme.colors.surfaceSecondary, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.lg, padding: theme.spacing.md, marginBottom: theme.spacing.md }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.xs }}>
                            <FontAwesomeIcon icon={faGem} style={{ color: theme.colors.text.secondary, fontSize: '16px', flexShrink: 0 }} />
                            <span style={{ fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary }}>Want more features?</span>
                        </div>
                        <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                            You're on the Basic plan. Whenever you see the <FontAwesomeIcon icon={faGem} style={{ fontSize: '12px' }} /> icon on a feature, click it to upgrade to Pro and unlock it.
                        </p>
                    </div>
                )}

                <button
                    type="button"
                    onClick={() => navigate('/auth/login')}
                    style={{ ...themedStyles.button.primary, width: '100%', padding: `${theme.spacing.md} ${theme.spacing.xl}`, textAlign: 'center' }}
                >
                    Sign In to Calculist
                </button>
            </div>
        );
    }

    return wrapper(
        <div style={{ ...themedStyles.card.default, maxWidth: '480px', width: '100%' }}>
            <SignupProgress currentStep={2} steps={USER_SIGNUP_STEPS} />

            <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                <img src="/logo-name.svg" alt="Calculist" style={{ height: '48px', width: 'auto', marginBottom: theme.spacing.md }} />
                <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Create your password</h1>
                <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>Choose a password to complete your account setup and join your team.</p>
            </div>

            <form onSubmit={(e) => void handleSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                    <label htmlFor="atip-pw" style={themedStyles.text.label}>Password</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            id="atip-pw"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                            autoComplete="new-password"
                            disabled={submitting}
                            placeholder="At least 8 characters"
                            style={{ ...themedStyles.input.default, paddingRight: '40px' }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(v => !v)}
                            tabIndex={-1}
                            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.text.secondary }}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                        </button>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                    <label htmlFor="atip-pw2" style={themedStyles.text.label}>Confirm password</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            id="atip-pw2"
                            name="confirm-password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                            disabled={submitting}
                            placeholder="Re-enter your password"
                            style={{ ...themedStyles.input.default, paddingRight: '40px' }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(v => !v)}
                            tabIndex={-1}
                            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.text.secondary }}
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                            <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                        </button>
                    </div>
                </div>

                {error && !expiredNotified && (
                    <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.status.error }}>{error}</span>
                )}
                {expiredNotified && (
                    <div style={{ backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '6px', padding: theme.spacing.md, fontSize: theme.typography.fontSize.sm, color: '#92400e' }}>
                        This invitation link has expired. Your team's admin contacts have been notified and can resend you a fresh invitation link.
                    </div>
                )}

                <button
                    type="submit"
                    disabled={!password || !confirmPassword || submitting}
                    style={{
                        ...themedStyles.button.primary,
                        ...(!password || !confirmPassword || submitting ? themedStyles.button.disabled : {}),
                        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                    }}
                >
                    {submitting ? 'Creating your account…' : 'Create Password'}
                </button>

                <SignupBackButton onClick={() => navigate(`/auth/complete-team-invite?token=${encodeURIComponent(token)}`)} disabled={submitting} />
            </form>
        </div>
    );
};
